import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Play, Zap, Share2, Crown, Sparkles, Check, TrendingUp, Eye, Rocket, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeatureCard = ({ icon: Icon, title, description }) => (
    <div className="card-base p-6 animate-slide-up">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Icon className="w-6 h-6 text-primary" />
        </div>
        <h3 className="font-heading text-lg font-semibold mb-2">{title}</h3>
        <p className="text-muted-foreground text-sm">{description}</p>
    </div>
);

// --- UPDATED PRICING CARD (Visually identical to Pricing.jsx) ---
// Updated PricingCard to include bonusCaption logic
const PricingCard = ({ title, price, period, features, isPro, isPopular, isBestValue, crossOutPrice, bonusCaption }) => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();

    return (
        <div className={`card-base p-8 relative transition-all duration-300 flex flex-col
            ${isPopular ? 'border-primary glow-primary-strong' : ''}
            ${isBestValue ? 'border-[#FF9E70] shadow-[0_0_25px_rgba(255,158,112,0.4)]' : ''} 
        `}>
            {/* LAUNCH SPECIAL BADGE */}
            {isPopular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-xs font-heading uppercase tracking-wider px-4 py-1.5 rounded-full">
                        Launch Special
                    </span>
                </div>
            )}

            {/* BEST VALUE BADGE */}
            {isBestValue && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-[#FF9E70] text-black text-xs font-heading uppercase tracking-wider px-4 py-1.5 rounded-full shadow-lg">
                        Best Value
                    </span>
                </div>
            )}

            <div className="text-center mb-8">
                {isPro && <Crown className={`w-8 h-8 mx-auto mb-3 ${isBestValue ? 'text-[#FF9E70]' : 'text-primary'}`} />}

                <h3 className="font-heading text-2xl uppercase mb-2">{title}</h3>

                {/* PRICE DISPLAY */}
                <div className="flex items-center justify-center gap-2">
                    {crossOutPrice && (
                        <span className="text-muted-foreground text-xl line-through decoration-destructive decoration-2">
                            {crossOutPrice}
                        </span>
                    )}
                    <div className="flex items-baseline gap-1">
                        <span className="font-brand text-5xl">{price}</span>
                        {period && <span className="text-muted-foreground">/{period}</span>}
                    </div>
                </div>

                {/* PRICE LOCK TEXT */}
                {(isPopular || isBestValue) && (
                    <p className={`text-xs mt-2 font-bold uppercase tracking-wide ${isBestValue ? 'text-[#FF9E70]' : 'text-primary'}`}>
                        Price locked while subscribed*
                    </p>
                )}
            </div>

            {/* FEATURES LIST */}
            <ul className="space-y-4 mb-8 flex-grow">
                {features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 
                            ${isBestValue ? 'bg-[#FF9E70]/20' : (isPro ? 'bg-primary/20' : 'bg-muted')} `}>
                            <Check className={`w-3 h-3 ${isBestValue ? 'text-[#FF9E70]' : (isPro ? 'text-primary' : 'text-muted-foreground')}`} />
                        </div>
                        <span className="text-sm text-muted-foreground">{feature}</span>
                    </li>
                ))}
            </ul>

            {/* NEW: BONUS CAPTION BLOCK */}
            {bonusCaption && (
                <div className="mb-6 px-1">
                    <p className={`text-xs text-center italic leading-relaxed ${isBestValue ? 'text-[#FF9E70]/90' : 'text-muted-foreground'}`}>
                        {bonusCaption}
                    </p>
                </div>
            )}

            {/* ACTION BUTTON */}
            <Button
                className={`w-full ${isPro
                        ? (isBestValue
                            ? 'bg-[#FF9E70] hover:bg-[#FF9E70]/90 text-black font-heading font-bold uppercase tracking-wider'
                            : 'btn-primary')
                        : 'btn-secondary'
                    }`}
                onClick={() => navigate(isAuthenticated ? '/pricing' : '/register')}
                data-testid={`pricing-${title.toLowerCase()}-btn`}
            >
                {isPro ? 'Lock In Rate' : 'Start Free'}
            </Button>
        </div>
    );
};

// --- TRENDING FEED (Unchanged) ---
const TrendingFeed = () => {
    const [trending, setTrending] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isHovered, setIsHovered] = useState(false); // Track hover state for desktop
    const navigate = useNavigate();

    // 1. Fetch Trending Data
    useEffect(() => {
        const fetchTrending = async () => {
            try {
                const response = await axios.get(`${API}/discovery?sort=trending&time_range=7d&limit=6`);
                if (response.data && response.data.length > 0) {
                    setTrending(response.data);
                } else {
                    const fallback = await axios.get(`${API}/discovery?sort=popular&time_range=all&limit=6`);
                    setTrending(fallback.data);
                }
            } catch (error) {
                console.error("Failed to load trending:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchTrending();
    }, []);

    // 2. Desktop-Only Auto-Rotation
    useEffect(() => {
        // Stop the timer if there's no data or the user is hovering
        if (trending.length === 0 || isHovered) return;

        const timer = setInterval(() => {
            // Crucial: Only update state if on a desktop-sized screen (Tailwind lg is 1024px)
            // This prevents mobile swipe-scrolling from "snapping back" due to component re-renders
            if (window.innerWidth >= 1024) {
                setCurrentIndex((prev) => (prev + 1) % trending.length);
            }
        }, 5000);

        // Cleanup interval on unmount
        return () => clearInterval(timer);
    }, [trending.length, isHovered]);

    if (loading || trending.length === 0) {
        return (
            <div className="relative">
                <div className="card-base aspect-video overflow-hidden glow-primary flex items-center justify-center bg-muted">
                    <div className="text-center">
                        <Play className="w-16 h-16 text-primary mx-auto mb-4 opacity-50" />
                        <p className="font-heading text-lg uppercase text-muted-foreground">Start Creating</p>
                    </div>
                </div>
            </div>
        );
    }

    const currentItem = trending[currentIndex];

    // Mobile: Horizontal Scrollable Carousel (Unchanged)
    const MobileCarousel = () => (
        <div className="lg:hidden">
            <div className="mb-4 flex items-center justify-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                <h3 className="font-heading text-sm uppercase text-primary">Trending Now</h3>
            </div>
            <div className="flex overflow-x-auto gap-4 snap-x snap-mandatory pb-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {trending.map((item, idx) => (
                    <div
                        key={item.id}
                        className="flex-shrink-0 w-[85vw] sm:w-80 snap-center"
                        onClick={() => navigate(`/watch/${item.id}`)}
                    >
                        <div className="card-base aspect-video overflow-hidden glow-primary relative cursor-pointer group">
                            {item.clip?.thumbnail_url ? (
                                <img
                                    src={item.clip.thumbnail_url}
                                    alt={item.title}
                                    className="w-full h-full object-cover transition-opacity duration-300 opacity-90 group-hover:opacity-100"
                                />
                            ) : (
                                <div className="w-full h-full bg-black/80 flex items-center justify-center">
                                    <Play className="w-12 h-12 text-white/20" />
                                </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent flex flex-col justify-end p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="bg-black/60 backdrop-blur text-white text-xs font-mono px-2 py-1 rounded flex items-center gap-1">
                                        <Eye className="w-3 h-3 text-primary" />
                                        {item.views.toLocaleString()}
                                    </span>
                                    <span className="text-xs text-white/70 font-mono">#{idx + 1}</span>
                                </div>
                                <h4 className="font-heading text-lg text-white uppercase leading-tight mb-1 drop-shadow-md line-clamp-2">
                                    {item.title}
                                </h4>
                                {item.creator_username && (
                                    <p className="text-xs text-white/90 font-medium mb-2 drop-shadow-md">
                                        by @{item.creator_username}
                                    </p>
                                )}
                                {item.subtitle_data?.[0] && (
                                    <div className="bg-white text-black px-2 py-1 rounded inline-block max-w-full truncate font-bold text-xs shadow-lg">
                                        "{item.subtitle_data[0].text.replace(/<[^>]+>/g, '')}"
                                    </div>
                                )}
                            </div>
                            <div className="absolute inset-0 scanline-overlay opacity-20 pointer-events-none" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // Desktop: Single Item Carousel (Added Hover Listeners)
    const DesktopCarousel = () => (
        <div
            className="relative hidden lg:block group"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="absolute -top-4 right-4 z-20 bg-primary text-primary-foreground text-xs font-bold uppercase px-3 py-1 rounded-full shadow-lg flex items-center gap-1 animate-bounce">
                <TrendingUp className="w-3 h-3" />
                Trending Now
            </div>
            <div
                className="card-base aspect-video overflow-hidden glow-primary relative cursor-pointer transition-transform duration-500 hover:scale-[1.02]"
                onClick={() => navigate(`/watch/${currentItem.id}`)}
            >
                {currentItem.clip?.thumbnail_url ? (
                    <img
                        src={currentItem.clip.thumbnail_url}
                        alt={currentItem.title}
                        className="w-full h-full object-cover transition-opacity duration-500 opacity-90 group-hover:opacity-100"
                    />
                ) : (
                    <div className="w-full h-full bg-black/80 flex items-center justify-center">
                        <Play className="w-16 h-16 text-white/20" />
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent flex flex-col justify-end p-6">
                    <div className="transform transition-all duration-500 translate-y-0">
                        <div className="flex items-center justify-between mb-2">
                            <span className="bg-black/60 backdrop-blur text-white text-xs font-mono px-2 py-1 rounded flex items-center gap-1">
                                <Eye className="w-3 h-3 text-primary" />
                                {currentItem.views.toLocaleString()}
                            </span>
                            <span className="text-xs text-white/70 font-mono">#{currentIndex + 1}</span>
                        </div>
                        <h3 className="font-heading text-2xl text-white uppercase leading-none mb-1 drop-shadow-md line-clamp-1">
                            {currentItem.title}
                        </h3>
                        {currentItem.creator_username && (
                            <p className="text-xs text-white/90 font-medium mb-3 drop-shadow-md">
                                by @{currentItem.creator_username}
                            </p>
                        )}
                        {currentItem.subtitle_data?.[0] && (
                            <div className="bg-white text-black px-3 py-2 rounded-lg inline-block max-w-full truncate font-bold text-sm shadow-xl transform -rotate-1 origin-bottom-left">
                                "{currentItem.subtitle_data[0].text.replace(/<[^>]+>/g, '')}"
                            </div>
                        )}
                    </div>
                </div>
                <div className="absolute inset-0 scanline-overlay opacity-20 pointer-events-none" />
            </div>
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                {trending.map((_, idx) => (
                    <button
                        key={idx}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentIndex ? 'bg-primary w-6' : 'bg-border hover:bg-primary/50'
                            }`}
                        onClick={() => setCurrentIndex(idx)}
                    />
                ))}
            </div>
        </div>
    );

    return (
        <>
            <MobileCarousel />
            <DesktopCarousel />
        </>
    );
};

export default function Landing() {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();

    return (
        <Layout>
            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex items-center overflow-hidden">
                <div className="absolute inset-0 hero-glow" />
                <div
                    className="absolute inset-0 opacity-20"
                    style={{
                        backgroundImage: `url('https://images.unsplash.com/photo-1765410856020-d09abb26dcbe?crop=entropy&cs=srgb&fm=jpg&q=85')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20 lg:py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-8 animate-slide-up">
                            <div
                                className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6 cursor-pointer hover:bg-primary/20 transition-colors duration-300"
                                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                                role="button"
                                tabIndex={0}
                            >
                                <Rocket className="w-4 h-4 text-primary animate-pulse" />
                                <span className="text-sm font-medium text-primary tracking-wide">
                                    EARLY ACCESS: Founder Pricing Live
                                </span>
                            </div>
                            <h1 className="font-brand text-5xl md:text-7xl uppercase leading-none tracking-wide">
                                <span className="text-foreground">What will they say next?</span>
                                <br />
                                <span className="text-primary">YOU DECIDE!</span>
                            </h1>

                            {/* Mobile Carousel positioned between title and subtext */}
                            <div className="lg:hidden">
                                <TrendingFeed />
                            </div>

                            <p className="text-lg text-muted-foreground max-w-lg">
                                Who the heck knows what they're saying? Apparently you do! Pick a clip, drop in your subtitles, and create viral-worthy content (or at least something to make your friends laugh)!
                            </p>
                            <div className="flex flex-col sm:flex-row items-center sm:justify-start gap-4 w-full">
                                <Button
                                    className="btn-primary text-lg px-8 py-6 w-full sm:w-auto"
                                    onClick={() => navigate(isAuthenticated ? '/gallery' : '/register')}
                                    data-testid="hero-cta-btn"
                                >
                                    <Play className="w-5 h-5 mr-2" />
                                    Start Creating
                                </Button>
                                <Button
                                    variant="outline"
                                    className="btn-secondary text-lg px-8 py-6 w-full sm:w-auto"
                                    onClick={() => navigate('/gallery')}
                                    data-testid="hero-gallery-btn"
                                >
                                    Browse Gallery
                                </Button>
                            </div>

                        </div>
                        {/* Desktop Carousel on the right side */}
                        <div className="hidden lg:block">
                            <TrendingFeed />
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-24 bg-card/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-brand text-4xl md:text-5xl uppercase mb-4">
                            Why <span className="text-primary">SubHack</span>?
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            Everything you need to create scroll-stopping content
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <FeatureCard
                            icon={Zap}
                            title="Lightning Fast"
                            description="No rendering. No waiting. Your subtitles sync in real-time as you type."
                        />
                        <FeatureCard
                            icon={Share2}
                            title="Instant Sharing"
                            description="Get a unique URL for every creation. Share anywhere, embed everywhere."
                        />
                        <FeatureCard
                            icon={Sparkles}
                            title="Remix Culture"
                            description="See a clip you like? Hit remix and make it yours with new captions."
                        />
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-brand text-4xl md:text-5xl uppercase mb-4">
                            Three Steps to <span className="text-primary">Hilarity</span>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { step: '01', title: 'Pick a Clip', desc: 'Browse our curated library of meme-worthy moments' },
                            { step: '02', title: 'Add Captions', desc: 'Type your text, drag to time it perfectly' },
                            { step: '03', title: 'Share & Go Viral', desc: 'Share your unique link and let the laughs begin!' },
                        ].map((item, idx) => (
                            <div key={idx} className="text-center animate-slide-up" style={{ animationDelay: `${idx * 100}ms` }}>
                                <div className="font-brand text-6xl text-primary/20 mb-4">{item.step}</div>
                                <h3 className="font-heading text-xl uppercase mb-2">{item.title}</h3>
                                <p className="text-muted-foreground text-sm">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Section - HARMONIZED */}
            <section className="py-24 bg-card/30" id="pricing">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-brand text-4xl md:text-5xl uppercase mb-4">
                            Founder <span className="text-primary">Pricing</span>
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            <span className="text-primary">Lock in these rates now.</span> Price increases coming soon!
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        <PricingCard
                            title="Free"
                            price="$0"
                            period=""
                            features={[
                                '1 active project',
                                'Links expire in 7 days',
                                'Standard subtitle style',
                                'Track your view count',
                                'SubHack watermark'
                            ]}
                            isPro={false}
                        />
                        <PricingCard
                            title="Pro Monthly"
                            price="$5"
                            crossOutPrice="$9.99"
                            period="month"
                            features={[
                                'Unlimited projects',
                                'Permanent links',
                                'All subtitle styles',
                                'Custom fonts & colors',
                                'No ads or watermarks',
                                'Download .SRT files',
                                'Priority Support'
                            ]}
                            isPro={true}
                            isPopular={true}
                        />

                        {/* UPDATED ANNUAL CARD */}
                        <PricingCard
                            title="Pro Annual"
                            price="$48"
                            crossOutPrice="$96"
                            period="year"
                            features={[
                                'Everything in Pro Monthly',
                                'Save 20% each year',
                                'Shape the product roadmap',
                                'Access to exclusive Vault clips'
                            ]}
                            isPro={true}
                            isBestValue={true} // Triggers Melon Orange
                            bonusCaption="As a founding annual subscriber, you'll join the inner circle and have a seat at the table. Access the hidden clip vault and have a direct voice in shaping our product roadmap!"
                        />
                    </div>
                </div>
            </section>

            {/* FINE PRINT */}
            <div className="max-w-2xl mx-auto text-center border-t border-border/50 pt-8 mb-16">
                <p className="text-[12px] text-muted-foreground/60 leading-relaxed">
                    * <strong>Founder Pricing Terms:</strong> The discounted Founder Pricing rate is locked in for the lifetime of your continuous subscription.
                    If you cancel your plan or your subscription lapses due to payment failure, you will forfeit the Founder Pricing rate
                    and will be subject to the current standard pricing upon any subsequent resubscription.
                </p>
            </div>

            {/* CTA Section */}
            <section className="py-24 relative overflow-hidden">
                <div className="absolute inset-0 hero-glow opacity-50" />
                <div className="relative max-w-4xl mx-auto px-4 text-center">
                    <h2 className="font-brand text-4xl md:text-6xl uppercase mb-6">
                        Ready to <span className="text-primary">Create</span>?
                    </h2>
                    <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                        Join the first wave of creators defining the next viral trends.
                    </p>
                    <Button
                        className="btn-primary text-lg px-12 py-6"
                        onClick={() => navigate(isAuthenticated ? '/gallery' : '/register')}
                        data-testid="cta-start-btn"
                    >
                        Start Creating for Free
                    </Button>
                </div>
            </section>
        </Layout>
    );
}